import {produce} from 'immer';
import reactUpdate, {Spec} from 'immutability-helper';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import * as context from 'Context';
import * as DataTableConfigEditUtil from 'notebook/data-tables/DataTableConfigEditUtil';
import {FromServerDataTable, FromServerDataTableConfig} from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {FromServerId, NLOToServerId, Selector, ToServerId} from 'notebook/ELNConstants';
import {fillEmptyDays} from 'notebook/entries/EntryDayOps';
import * as NoteLinkedAppCanvasUtil from 'notebook/integrations/benchling-apps/NoteLinkedAppCanvasUtil';
import {NoteLinkedChart} from 'notebook/integrations/charts/NoteLinkedChartPlugin';
import {
  FromServerNoteLinkedPlateDesign,
  NoteLinkedPlateDesign,
} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignTypes';
import * as NoteLinkedPlateDesignUtil from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignUtil';
import * as NoteLinkedPlateRecordUtil from 'notebook/integrations/plate-maps/plate-record/NoteLinkedPlateRecordUtil';
import * as NoteLinkedScratchPlateDesignUtil from 'notebook/integrations/plate-maps/scratch-plates/NoteLinkedScratchPlateDesignUtil';
import {Format, FormatInternalLink} from 'notebook/mediocre/formatted-text/FormattersConstants';
import * as NoteLinkedFileUtils from 'notebook/notes/attachments/NoteLinkedFileUtil';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {FromServerNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import * as NoteLinkedGroupUtil from 'notebook/notes/groups/util/NoteLinkedGroupStoreTransforms';
import {MAX_INDENTATION} from 'notebook/notes/lists/ListConstants';
import updateCalculatedIndices from 'notebook/notes/lists/updateCalculatedIndices';
import NoteConstants, {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteFillInTextBox} from 'notebook/notes/plugins/fill-in-text-box/constants/FillInTextBoxConstants';
import * as FillInTextBoxUtils from 'notebook/notes/plugins/fill-in-text-box/FillInTextBoxUtils';
import {findPlugin} from 'notebook/notes/plugins/NoteEditorPluginRegistry';
import SelectorDAO from 'notebook/plain-tables/selectors/SelectorDAO';
import {
  FromServerRegistrationTable,
  FromServerRegistrationTableConfig,
} from 'notebook/registration-tables/RegistrationTableConstants';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import {addCid} from 'notebook/store/StoreTransformUtil';
import prepareStructuredTableMappingsForSave from 'notebook/structured-tables/structured-table-mappings/prepareStructuredTableMappingsForSave';
import prepareStructuredTableMappingsForStore from 'notebook/structured-tables/structured-table-mappings/prepareStructuredTableMappingsForStore';
import {StructuredTableMappingsStateManager} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {FromServerStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import * as StructuredTableStoreTransforms from 'notebook/structured-tables/util/StructuredTableStoreTransforms';
import augmentNotePartTablesWithValues from 'notebook/tables/augmentNotePartTablesWithValues';
import {
  createNoteRuntimeSnapshot,
  prepareNoteRuntimeSnapshotForSave,
  prepareNoteRuntimeSnapshotForStore,
} from 'notebook/tables/TableStoreTransforms';
import TableUtil from 'notebook/tables/TableUtil';
import {
  FromServerTransferTableConfig,
  FromServerTransferTableModel,
} from 'notebook/transfer-tables/TransferTableConstants';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import {
  FromServerNoteLinkedObject,
  Note,
  NoteLinkedFile,
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
  ServerNote,
  ServerNotePart,
} from 'notebook/util/NoteUtilConstants';
import {getStore} from 'redux-stores/Store';
import {FromServerNoteLinkedAssayData} from 'runs/AttachmentConstants';
import * as NoteLinkedAssayDataUtil from 'runs/NoteLinkedAssayDataUtil';
import EntityStore from 'stores/EntityStore';
// @ts-expect-error
import TrayTaskStore from 'tray/TrayTaskStore';
import * as TrayTaskUtil from 'tray/TrayTaskUtil';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';
import {assertNever} from 'util/Types';
import {WorkflowSelector} from 'workflows/reduceWorkflows';

// Wraps an item during load/update, can be used to insert default empty items
export function sanitizeNote<T extends Note>(note: T, oldNote: T | null = null): T {
  // If the note hasn't changed, we can skip the sanitization step. If we don't skip, the NoteEditor
  // will re-render unnecessarily.
  if (oldNote != null && note === oldNote) {
    return note;
  }

  let newNoteParts = note.noteParts;
  const shouldUpdateTimestamp = oldNote != null || newNoteParts.length === 0;

  // Fills note if empty
  if (newNoteParts.length === 0) {
    newNoteParts = [makeDefaultItem()];
  }

  if (shouldUpdateTimestamp) {
    const oldPartMap = _.indexBy(oldNote?.noteParts || [], 'cid');
    newNoteParts = newNoteParts.map((notePart) => {
      const {cid} = notePart;
      const oldNotePart = oldPartMap[cid];
      if (
        !context.get('skipBumpVersionForNonEdit') &&
        (oldNotePart == null || !isEqual(notePart, oldNotePart))
      ) {
        notePart = reactUpdate(notePart, {
          $merge: {
            creator: EntityStore.currentUser(),
            modified_at: new Date().toISOString(),
          },
        });
      }
      const badIndentationMsg = `Bad indentation ${notePart.indentation}`;
      if (
        !assertAndContinue(
          0 <= notePart.indentation && notePart.indentation <= MAX_INDENTATION,
          badIndentationMsg
        )
      ) {
        notePart = reactUpdate(notePart, {indentation: {$set: 0}});
      }

      const badListIndentationMsg = `Bad indentation ${notePart.indentation} on ${notePart.listType}`;
      if (notePart.listType != null) {
        if (!assertAndContinue(notePart.indentation >= 1, badListIndentationMsg)) {
          notePart = reactUpdate(notePart, {indentation: {$set: 1}});
        }
      }

      return notePart;
    });
  }

  const structuredTableMappings = sanitizeStructuredTableMappings(note);

  note = reactUpdate(note, {$merge: {noteParts: newNoteParts, structuredTableMappings}} as Spec<T>);
  if (note.days != null) {
    // @ts-expect-error(2322): reassigning to a new type is difficult
    note = fillEmptyDays(note);
  }

  // Add calculatedListIndex so noteParts are not context dependent (you don't
  // need the whole note to render a note part)
  newNoteParts = updateCalculatedIndices(note.noteParts, note.days);
  note = reactUpdate(note, {$merge: {noteParts: newNoteParts}} as Spec<T>);
  return note;
}

function sanitizeStructuredTableMappings<T extends Note>(note: T): T['structuredTableMappings'] {
  const mappings = note.structuredTableMappings;
  if (!mappings?.length) {
    return mappings;
  }

  return StructuredTableMappingsStateManager.removeInvalidColumnMappings(
    StructuredTableMappingsStateManager.setStructuredTableMappings(mappings),
    getAllStructuredTablesInNote(note)
  ).structuredTableMappings;
}

export function addTasksToNote<T extends Note>(note: T): T {
  const noteParts = note.noteParts.map((notePart) => {
    // eslint-disable-next-line benchlint/no-in-operator
    if ('taskId' in notePart) {
      // eslint-disable-next-line no-restricted-syntax
      return _.extend({task: _.omit(TrayTaskStore.getTask(notePart.taskId), 'icon')}, notePart);
    }
    return notePart;
  });

  // If all note parts are equal, avoid update and return original note to prevent unnecessary updates.
  if (isEqual(noteParts, note.noteParts)) {
    return note;
  }

  const versionedNote = reactUpdate(note, {noteParts: {$set: noteParts}} as Spec<T>);
  return versionedNote;
}

export function removeTasksFromNote<T extends Note>(note: T): T {
  function maybeRemoveTask(obj: NotePart & {taskId?: string}): NotePart {
    // TODO: When we store results for tasks, we should set the result on here, though there may be multiple
    // keys that need to be updated (e.g. setting placeholder to false). We assume that when a task has
    // completed, the associated note part will never be updated with the result again, so redoing an undone
    // 'insert file' operation will leave a permanently 'uploading' file (even though the file has finished).
    // Thus, we need to delete the task from it (so that the component turns into a general placeholder) if
    // the task has finished. Otherwise, we can leave the task.
    // We don't want to delete the task in the general case because if a user undoes an insert text operation
    // on the same note part, the task (while uploading) will get removed and the component will cahnge to a
    // general placeholder which is not desirable.
    if (obj.taskId == null) {
      return obj;
    }

    const task = TrayTaskStore.getTask(obj.taskId);
    if (task == null || TrayTaskUtil.hasTaskFinished(task)) {
      obj = _.clone(obj);
      delete obj.taskId;
      delete obj.task;
    }
    return obj;
  }

  const noteParts = Array.from(note.noteParts).map((notePart) => maybeRemoveTask(notePart));
  const versionedNote = reactUpdate(note, {noteParts: {$set: noteParts}} as Spec<T>);
  return versionedNote;
}

export function pickRevertableFields(note: Note): Partial<Note> {
  return pick(note, 'noteParts');
}

export function prepareNLOForStore<T extends FromServerNoteLinkedObject>(
  notePart: ServerNotePart & {item: T},
  cidIdMap: CidIdMap
): void {
  const {item} = notePart;

  const plugin = item?.type && findPlugin(item.type);
  if (plugin && plugin.prepareForStore) {
    plugin.prepareForStore(item, cidIdMap);
    return;
  }

  switch (item.type) {
    case NoteLinkedObjectType.DATA_TABLE:
      DataTableUtil.prepareDataTableForStore(item as FromServerDataTable, cidIdMap);
      break;
    case NoteLinkedObjectType.DATA_TABLE_CONFIG:
      DataTableConfigEditUtil.prepareDataTableConfigForStore(item as FromServerDataTableConfig, cidIdMap);
      break;
    case NoteLinkedObjectType.NOTE_FILL_IN_TEXT_BOX:
      FillInTextBoxUtils.prepareForStore(item as unknown as NoteFillInTextBox, cidIdMap);
      break;
    case NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA:
      NoteLinkedAssayDataUtil.prepareForStore(item as FromServerNoteLinkedAssayData, cidIdMap);
      break;
    case NoteLinkedObjectType.NOTE_LINKED_FILE:
      NoteLinkedFileUtils.prepareForStore(item as unknown as NoteLinkedFile, cidIdMap);
      break;
    case NoteLinkedObjectType.NOTE_LINKED_GROUP:
      NoteLinkedGroupUtil.prepareForStore(item as unknown as FromServerNoteLinkedGroup<unknown>, cidIdMap);
      break;
    case NoteLinkedObjectType.REGISTRATION_TABLE:
      RegistrationTableUtil.prepareForStore(item as FromServerRegistrationTable, cidIdMap);
      break;
    case NoteLinkedObjectType.REGISTRATION_TABLE_CONFIG:
      RegistrationTableUtil.prepareConfigForStore(item as FromServerRegistrationTableConfig, cidIdMap);
      break;
    case NoteLinkedObjectType.STRUCTURED_TABLE:
      StructuredTableStoreTransforms.prepareForStore(
        item as unknown as FromServerStructuredTable<unknown, unknown>,
        cidIdMap
      );
      break;
    case NoteLinkedObjectType.TRANSFER_TABLE:
      TransferTableUtil.prepareForStore(item as FromServerTransferTableModel, cidIdMap);
      break;
    case NoteLinkedObjectType.TRANSFER_TABLE_CONFIG:
      TransferTableUtil.prepareConfigForStore(item as FromServerTransferTableConfig, cidIdMap);
      break;
    case NoteLinkedObjectType.NOTE_LINKED_PLATE_RECORD:
      NoteLinkedPlateRecordUtil.prepareForStore(
        item as unknown as NoteLinkedPlateRecordUtil.NoteLinkedPlateRecord,
        cidIdMap
      );
      break;
    case NoteLinkedObjectType.NOTE_LINKED_PLATE_DESIGN:
      NoteLinkedPlateDesignUtil.prepareForStore(item as FromServerNoteLinkedPlateDesign, cidIdMap);
      break;
    case NoteLinkedObjectType.NOTE_LINKED_APP_CANVAS:
      NoteLinkedAppCanvasUtil.prepareForStore(
        item as NoteLinkedAppCanvasUtil.FromServerNoteLinkedAppCanvas,
        cidIdMap
      );
      break;
    case NoteLinkedObjectType.NOTE_LINKED_SCRATCH_PLATE_DESIGN:
      NoteLinkedScratchPlateDesignUtil.prepareForStore(
        item as NoteLinkedScratchPlateDesignUtil.FromServerNoteLinkedScratchPlateDesign,
        cidIdMap
      );
      break;
    default:
      addCid(item, 'note-linked-object-', cidIdMap);
  }
}

export function prepareNLOForSave(
  notePart: NotePart,
  cidIdMap: CidIdMap,
  fileId?: string
): NLOToServerId | null {
  if (!(notePart.type === NoteConstants.NOTE_LINKED_OBJECT && notePart.item != null)) {
    return null;
  }

  const {item} = notePart as NotePartWithItem<NoteLinkedObject>;

  const plugin = item?.type && findPlugin(item.type);
  if (plugin && plugin.prepareForSave) {
    return plugin.prepareForSave(item, cidIdMap, fileId);
  }

  const {type} = item;
  switch (type) {
    case NoteLinkedObjectType.DATA_TABLE:
      return DataTableUtil.prepareDataTableForSave(item, cidIdMap);
    case NoteLinkedObjectType.DATA_TABLE_CONFIG:
      return DataTableUtil.prepareDataTableConfigForSave(item, cidIdMap);
    case NoteLinkedObjectType.NOTE_FILL_IN_TEXT_BOX:
      return FillInTextBoxUtils.prepareForSave(item, cidIdMap);
    case NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA:
      return NoteLinkedAssayDataUtil.prepareForSave(item, cidIdMap);
    case NoteLinkedObjectType.NOTE_LINKED_GROUP:
      return NoteLinkedGroupUtil.prepareForSave(item, cidIdMap, fileId);
    case NoteLinkedObjectType.REGISTRATION_TABLE:
      return RegistrationTableUtil.prepareForSave(item, cidIdMap);
    case NoteLinkedObjectType.REGISTRATION_TABLE_CONFIG:
      return RegistrationTableUtil.prepareConfigForSave(item, cidIdMap);
    case NoteLinkedObjectType.STRUCTURED_TABLE:
      return StructuredTableStoreTransforms.prepareForSave<unknown, unknown>({...item, fileId}, cidIdMap);
    case NoteLinkedObjectType.TRANSFER_TABLE:
      return TransferTableUtil.prepareForSave(item, cidIdMap);
    case NoteLinkedObjectType.TRANSFER_TABLE_CONFIG:
      return TransferTableUtil.prepareConfigForSave(item, cidIdMap);
    case NoteLinkedObjectType.NOTE_LINKED_PLATE_DESIGN:
      return NoteLinkedPlateDesignUtil.prepareForSave(item as NoteLinkedPlateDesign, cidIdMap);
    case NoteLinkedObjectType.NOTE_LINKED_PLATE_RECORD:
      return NoteLinkedPlateRecordUtil.prepareForSave(item, cidIdMap);
    case NoteLinkedObjectType.NOTE_LINKED_APP_CANVAS:
      return NoteLinkedAppCanvasUtil.prepareForSave(
        item as NoteLinkedAppCanvasUtil.NoteLinkedAppCanvas,
        cidIdMap
      ) as unknown as NLOToServerId;
    case NoteLinkedObjectType.NOTE_LINKED_SCRATCH_PLATE_DESIGN:
      return NoteLinkedScratchPlateDesignUtil.prepareForSave(
        item as NoteLinkedScratchPlateDesignUtil.NoteLinkedScratchPlateDesign,
        cidIdMap
      ) as unknown as NLOToServerId;
    case NoteLinkedObjectType.NOTE_LINKED_CHART: {
      const chartItem = item as unknown as NoteLinkedChart;
      return {
        id: cidIdMap[item.cid],
        type: item.type,
        cid: item.cid,
        name: chartItem.name,
        chart: chartItem.chart,
      } as unknown as NLOToServerId;
    }
    case NoteLinkedObjectType.NOTE_LINKED_FILE:
    case NoteLinkedObjectType.NOTE_LINKED_DATA:
    case NoteLinkedObjectType.NOTE_LINKED_EMBEDDING: {
      const nloId = cidIdMap[item.cid];
      const nlf = item as NoteLinkedFile;
      return {
        id: nloId,
        type: item.type,
        cid: item.cid,
        name: nlf.name,
        tfiFileMeta: nlf.tfiFileMeta || null,
        externalFileExcerpt: nlf.externalFileExcerpt?.id || null,
        uploadSource: nlf.uploadSource,
      } as unknown as NLOToServerId;
    }
    default:
      return assertNever(type);
  }
}

export function prepareNotePartForStore(notePart: ServerNotePart, cidIdMap: CidIdMap): void {
  addCid(notePart, 'note-part', cidIdMap);
  if (notePart.headerType == null) {
    notePart.headerType = NoteConstants.PARAGRAPH;
  }
  if (notePart.table != null) {
    notePart.table = TableUtil.prepareForStore(notePart.table);
  }
  if (notePart.type === NoteConstants.NOTE_LINKED_OBJECT && notePart.item != null) {
    prepareNLOForStore(notePart as ServerNotePart & {item: FromServerNoteLinkedObject}, cidIdMap);
  }
}

export function prepareForStore(note: ServerNote<FromServerId>, cidIdMap: CidIdMap, fileId: string): Note {
  addCid(note, 'note-version', cidIdMap);
  note.selectors = note.selectors.map((selector) => SelectorDAO.prepareForStore(selector, cidIdMap));
  if (note.noteRuntimeSnapshot) {
    prepareNoteRuntimeSnapshotForStore(note.noteRuntimeSnapshot, cidIdMap);
  } else {
    (note as unknown as Note).noteRuntimeSnapshot = createNoteRuntimeSnapshot();
  }
  for (const notePart of Array.from(note.noteParts)) {
    prepareNotePartForStore(notePart as ServerNotePart, cidIdMap);
  }
  // Only set Table Mappings on the Note level if this is a subtemplate.
  const isSubtemplate = fileId && APIUtil.isSubtemplate(fileId);
  if (isSubtemplate) {
    note.structuredTableMappings = prepareStructuredTableMappingsForStore(
      fileId,
      cidIdMap,
      note.structuredTableMappings ?? []
    );
  }
  return note as unknown as Note;
}

export function prepareNotePartForSave(notePart: NotePart, cidIdMap: CidIdMap, fileId?: string): NotePart {
  // eslint-disable-next-line no-restricted-syntax
  const baseResult = _.extend(
    pick(
      notePart,
      'cid',
      'alignment',
      'important',
      'item_id',
      'text',
      'type',
      'table',
      'formats',
      'listType',
      'headerType',
      'indentation',
      'placeholder',
      'code',
      'itemWidth'
    ),
    {
      id: cidIdMap[notePart.cid],
      item: prepareNLOForSave(notePart, cidIdMap, fileId),
      headerType: notePart.headerType === NoteConstants.PARAGRAPH ? null : notePart.headerType,
    }
  );
  // When reverting to a version with frozen chips (or generating a version that has erroneous frozen info),
  // we don't want to save that information as it will lead to chips being frozen when they shouldn't.
  // Under no conditions should we be saving the cachedDisplayInfo across versions.
  if (baseResult.formats.internalLink && baseResult.formats.internalLink.length !== 0) {
    baseResult.formats = produce(baseResult.formats, (draftState: Format) => {
      draftState.internalLink.forEach((internalLink: FormatInternalLink) => {
        // @ts-expect-error
        internalLink.data.cachedDisplayInfo = null;
      });
    });
  }
  return baseResult;
}

export function prepareForSave(note: Note, cidIdMap: CidIdMap, fileId: string): ServerNote<ToServerId> {
  const evaluationSnapshot = note.noteRuntimeSnapshot?.evaluationSnapshot;
  // We should always evaluate before saving, unless there is already an evaluation snapshot we can use.
  // At this point, there should definitely be an evaluation snapshot.
  // (Confirmed that this works even for "bootstrap" entries, which are entries that already existed before Persist Eval was enabled.
  // Bootstrap entries don't have an evaluation snapshot right away, but evaluation does happen at some point before this
  // prepareForSave method is called.)
  assertAndContinue(
    evaluationSnapshot != null,
    'Evaluation snapshot is missing. Falling back to live evaluation for syncing of legacy results tables.'
  );
  const noteParts = augmentNotePartTablesWithValues(
    note.noteParts,
    fileId,
    evaluationSnapshot
      ? {sourceType: 'EVALUATION_SNAPSHOT', evaluationSnapshot}
      : {
          sourceType: 'LIVE_EVALUATION',
          // It's OK to not have a store dependency for the stage runs, because changing stage runs always refreshes
          // the workflow entry.
          stageRuns: new WorkflowSelector(getStore().getState().models.workflows).getOrderedStageRunsForEntry(
            fileId
          ),
        }
  );

  const isSubtemplate = Boolean(fileId) && APIUtil.isSubtemplate(fileId);
  return {
    id: cidIdMap[note.cid] as number,
    cid: note.cid,
    noteRuntimeSnapshot: note.noteRuntimeSnapshot
      ? prepareNoteRuntimeSnapshotForSave(note.noteRuntimeSnapshot, cidIdMap)
      : null,
    selectors: note.selectors.map((selector) =>
      SelectorDAO.prepareForSave(selector, cidIdMap)
    ) as unknown as Array<Selector>,
    noteParts: noteParts.map((notePart) => prepareNotePartForSave(notePart, cidIdMap, fileId)),
    // only save table mappings for subtemplates, other types of document should not have Table Mappings
    // at the Note level
    ...(isSubtemplate && {structuredTableMappings: prepareStructuredTableMappingsForSave(fileId, cidIdMap)}),
  } as unknown as ServerNote<ToServerId>;
}
